/**
 * Data processing and rendering functions for each type of chart
 */

import { line, area, curveBasis, curveCardinal } from 'd3-shape'
import { scaleLinear, scaleSymlog, scaleBand } from 'd3-scale'
import { select, mouse } from 'd3-selection'
import { bisector } from 'd3-array'
import { timeFormat } from 'd3-time-format'
import { format } from 'd3-format'
import { interpolate } from 'd3-interpolate'

const timeFormatter = timeFormat( '%B %d, %Y' )
const formatComma = format( ',' )

/**
 * Line graph
 * Cumulative day over day total cases / deaths / recovered
 */
const cumulative = {
	data: null,
	showTests: false,
	bisector: null,
	yScale: null,
	yScaleTicks: null,
	yScaleValues: null,
	bounds: {
		minY: 0,
		maxY: 0,
		minX: 0,
		maxX: 0
	},

	init( data, dataType, showTests, logScale ) {
		if ( null === this.data ) {
			let minY = Infinity
			let maxY = 0

			// process data
			this.data = data.map( ( item, index ) => {
				const value = parseInt( item[ dataType ].replace(/[\*]*/, ''), 10 )
				const tests = parseInt( item['tests'], 10 )
				const date = new Date( item.date )

				minY = Math.min( minY, value )
				maxY = Math.max( maxY, value )

				return {
					'date': date,
					'value': value,
					'tests': tests
				}
			})

			// initialize bounds
			this.bounds.minY = minY
			this.bounds.maxY = maxY
			this.bounds.minX = this.data[0].date
			this.bounds.maxX = this.data[ this.data.length - 1 ].date

			// initialize bisector
			this.bisector = bisector( ( item ) => item.date ).left

			// initialize y scale
			this.yScale = scaleLinear()

			// y scal tick count
			this.yScaleTicks = 4

			if ( logScale ) {
				this.bounds.minY = 0 === minY
				? 0 : Math.pow( 10, Math.floor( minY ).toString().length - 1 )
				this.bounds.maxY = Math.pow( 10, Math.floor( maxY ).toString().length )

				// construct logarithmic scale
				this.yScale = scaleSymlog()

				// y scale values
				this.yScaleValues = []
				for ( let i = this.bounds.minY.toString().length - 1; i < this.bounds.maxY.toString().length; i++ ) {
					const value = 0 === i ? 0 : Math.pow( 10, i )
					this.yScaleValues.push( value )
				}

				// y scal tick count
				this.yScaleTicks = this.yScaleValues.length
			}

			// show tests setting
			this.showTests = showTests
		}
	},

	render( frame ) {
		// Remove existing chart content
		frame.chart.svg.selectAll( '.c-chart__content' )
			.remove()

		// Draw line
		let path = line()
			.x( ( item ) => frame.chart.xScale( item.date ) )
			.y( ( item ) => this.yScale( item.value ) )

		frame.chart.elem.append( 'path' )
			.attr( 'class', 'c-chart__line c-chart__content' )
			.attr( 'fill', 'none' )
			.attr( 'stroke', frame.colors[ frame.dataType ] )
			.attr( 'stroke-width', 2 )
			.attr( 'd', path( this.data ) )

		// Tooltip logic
		const focus = frame.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__focus c-chart__content' )
			.style( 'display', 'none' )

		focus.append( 'line' )
			.attr( 'stroke', frame.colors[ frame.dataType ] )
			.attr( 'stroke-width', 2 )
			.attr( 'stroke-dasharray', 4 )
			.attr( 'x1', 0 )
			.attr( 'y1', frame.margin.top )
			.attr( 'x2', 0 )
			.attr( 'y2', frame.margin.top + frame.chart.height - 10 )

		focus.append( 'circle' )
			.attr( 'r', 5 )
			.attr( 'fill', '#FFF' )
			.attr( 'stroke', frame.colors[ frame.dataType ] )
			.attr( 'stroke-width', 2 )

		 frame.chart.elem.append( 'rect' )
		 	.attr( 'class', 'c-chart__overlay c-chart__content' )
		 	.attr( 'width', frame.chart.width )
		 	.attr( 'height', frame.chart.height )
		 	.attr( 'fill', '#fff' )
		 	.on( 'mousemove', function( item ) {
		 		// intersection logic
		 		const xPos = mouse( this )[0]
		 		const xVal = frame.chart.xScale.invert( xPos )
		 		const i = cumulative.bisector( cumulative.data, xVal, 1 )
		 		const itemPrev = cumulative.data[ i - 1 ]
		 		const itemNext = i < cumulative.data.length ? cumulative.data[ i ] : itemPrev
		 		const itemSelected = xVal - itemPrev.date > itemNext.date - xVal ? itemNext : itemPrev

		 		// tooltip variables
				const chartLeftOffset = frame.$chart.getBoundingClientRect().left
				const chartTopOffset = frame.$chart.getBoundingClientRect().top
				const itemLeftOffset = frame.chart.xScale( itemSelected.date )
				const itemTopOffset = cumulative.yScale( itemSelected.value )
				const leftOffset = chartLeftOffset + itemLeftOffset
				const topOffset = chartTopOffset + itemTopOffset
				const documentWidth = document.documentElement.clientWidth

				let testingInfo
				if ( false === cumulative.showTests ) {
					testingInfo = ''
				} else if ( isNaN( itemSelected.tests ) || 0 === itemSelected.tests ) {
					testingInfo = '<br>(no testing data)'
				} else {
					testingInfo = '<br>out of <strong> ' + formatComma( itemSelected.tests ) + '</strong> tests'
				}

		 		focus.style( 'display', 'inline-block' )
		 			.attr( 'transform', `translate(${frame.chart.xScale( itemSelected.date )}, 0)`)

		 		focus.select( 'circle' )
		 			.attr( 'transform', `translate( 0, ${cumulative.yScale( itemSelected.value )})`)

		 		frame.chart.tooltip
					.style( 'display', 'inline-block' )
					.style( 'visibility', 'hidden' )
					.html( timeFormatter( itemSelected.date ) + '<br><strong>' + formatComma( itemSelected.value ) + '</strong> total ' + frame.typeFormatted + testingInfo )

				const tipWidth = frame.$tip.offsetWidth
				const tipHeight = frame.$tip.offsetHeight
				let tipLeftOffset = Math.max( leftOffset - tipWidth, 0 )
				let tipTopOffset = Math.max( topOffset - ( tipHeight * 1.5 ), chartTopOffset )

				frame.chart.tooltip
					.style( 'left', tipLeftOffset + 'px' )
					.style( 'top', tipTopOffset + 'px' )
					.style( 'display', 'inline-block' )
					.style( 'visibility', 'visible' )
			})
			.on( 'mouseout', ( item ) => {
				focus.style( 'display', 'none' )
				frame.chart.tooltip.style( 'display', 'none' )
			})
	}
}

/**
 * Histogram graph
 * Day over day new cases / deaths / recovered
 */
const histogram = {
	data: null,
	showTests: false,
	bandScale: null,
	yScale: null,
	yScaleTicks: 4,
	yScaleValues: null,
	bounds: {
		minY: 0,
		maxY: 0,
		minX: 0,
		maxX: 0
	},

	init( data, dataType, showTests ) {
		if ( null === this.data ) {
			let minY = Infinity
			let maxY = 0
			let week = []

			// process data
			this.data = data.map( ( item, index ) => {
				if ( index > 0 ) {
					// total
					const current = parseInt( item[ dataType ].replace(/[\*]*/, ''), 10 )
					const prev = parseInt( data[index - 1][ dataType ].replace(/[\*]*/, ''), 10 )
					const diff = current - prev

					// tests
					const currentTests = parseInt( item['tests'], 10 )
					const prevTests = parseInt( data[index - 1]['tests'], 10 )
					const testsDiff = currentTests - prevTests

					// 7-day average
					const weekLength = week.unshift( diff )
					if ( weekLength > 7 ) {
						week.pop()
					}
					const weekSum = week.reduce( ( a, b ) => a + b, 0 )
					const weekAvg = ( weekSum / week.length ) || 0

					minY = Math.min( minY, diff )
					maxY = Math.max( maxY, diff )

					return {
						'date': item['date'],
						'dateFormatted': new Date( item['date'] ),
						'value': diff,
						'average': weekAvg,
						'tests': testsDiff
					}
				}
			}).slice(1)

			// initialize bounds
			this.bounds.minY = minY
			this.bounds.maxY = maxY
			this.bounds.minX = new Date( this.data[0].date )
			this.bounds.maxX = new Date( this.data[ this.data.length - 1 ].date )

			// initialize histogram scale
			this.bandScale = scaleBand()

			// initialize y scale
			this.yScale = scaleLinear()

			// show tests setting
			this.showTests = showTests
		}
	},

	render( frame ) {
		// Histogram bars
		this.bandScale.range([0, frame.chart.width])
			.domain( this.data.map( ( item ) => item.date ) )
			.paddingInner( 0.2 )

		// Remove existing chart content
		frame.chart.elem.selectAll( '.c-chart__content' )
			.remove()

		// Draw backing area
		let areaPath = area()
			.x( ( item ) => frame.chart.xScale( item.dateFormatted ) )
			.y0( frame.chart.height )
			.y1( ( item ) => this.yScale( item.average ) )
			.curve( curveBasis )

		frame.chart.elem.append( 'path' )
			.attr( 'class', 'c-chart__area c-chart__content' )
			.attr( 'fill', frame.colors[ frame.dataType ] )
			.attr( 'd', areaPath( this.data ) )

		// Add histogram bars
		frame.chart.elem.selectAll()
			.data( this.data )
			.enter()
				.append( 'rect' )
				.attr( 'class', 'c-chart__bar c-chart__content' )
				.attr( 'x', ( item ) => this.bandScale( item.date ) )
				.attr( 'y', ( item ) => this.yScale( item.value ) )
				.attr( 'height', ( item ) => frame.chart.height - this.yScale( item.value ) )
				.attr( 'width', this.bandScale.bandwidth() )
				.attr( 'fill', frame.colors[ frame.dataType ] )
			.on( 'mouseover', function ( item ) {
				const currentBar = select( this )
				const chartLeftOffset = frame.$chart.getBoundingClientRect().left
				const chartTopOffset = frame.$chart.getBoundingClientRect().top
				const barLeftOffset = parseInt( currentBar.attr( 'x' ), 0 )
				const barTopOffset = parseInt( currentBar.attr( 'y' ), 0 )
				const barWidth = parseInt( currentBar.attr( 'width' ), 0 )
				const leftOffset = chartLeftOffset + barLeftOffset
				const topOffset = chartTopOffset + barTopOffset
				const documentWidth = document.documentElement.clientWidth

				let testingInfo
				if ( false === histogram.showTests ) {
					testingInfo = ''
				} else if ( isNaN( item.tests ) || item.tests <= 0 ) {
					testingInfo = '<br>(no testing data)'
				} else {
					testingInfo = '<br>out of <strong>' + formatComma( item.tests ) + '</strong> new tests'
				}

				frame.chart.tooltip
					.style( 'display', 'inline-block' )
					.style( 'visibility', 'hidden' )
					.html( timeFormatter( new Date( item.date ) ) + '<br><strong>' + formatComma( item.value ) + '</strong> new ' + frame.typeFormatted + testingInfo )

				const tipWidth = frame.$tip.offsetWidth
				const tipHeight = frame.$tip.offsetHeight
				let tipLeftOffset = barWidth + leftOffset - ( tipWidth / 2 )
				let tipTopOffset = topOffset - tipHeight

				if ( tipLeftOffset + tipWidth > documentWidth ) {
					tipLeftOffset = documentWidth - tipWidth
				} else if ( tipLeftOffset < 0 ) {
					tipLeftOffset = 0
				}
				
				frame.chart.tooltip
					.style( 'left', tipLeftOffset + 'px' )
					.style( 'top', tipTopOffset + 'px' )
					.style( 'visibility', 'visible' )

				currentBar.node().classList.add( 'c-chart__bar--hovered' )
			})
			.on( 'mouseout', function ( item ) {
				frame.chart.tooltip.style( 'display', 'none' )
				select( this ).node().classList.remove( 'c-chart__bar--hovered' )
			})

		// Draw average trend line
		let path = line()
			.x( ( item ) => frame.chart.xScale( item.dateFormatted ) )
			.y( ( item ) => this.yScale( item.average ) )
			.curve( curveBasis )

		frame.chart.elem.append( 'path' )
			.attr( 'class', 'c-chart__line c-chart__line--trend c-chart__content' )
			.attr( 'fill', 'none' )
			.attr( 'stroke', frame.colors[ frame.dataType ] )
			.attr( 'stroke-width', 2 )
			.attr( 'pointer-events', 'none' )
			.attr( 'd', path( this.data ) )

		// Add label
		const labelPoint = this.data[20]
		const labelX = this.bandScale( labelPoint.date )
		const labelY = this.yScale( labelPoint.average )

		let labelGroup = frame.chart.elem.append('text')
			.attr( 'class', 'c-chart__label u-desktop c-chart__content' )
			.attr( 'text-anchor', 'middle' )
			.attr( 'fill', '#767676' )
			.attr( 'transform', `translate(${labelX - 20},${labelY - 60})`)
			
		labelGroup.append( 'tspan' )
			.text( 'line shows' )

		labelGroup.append( 'tspan' )
			.attr( 'x', 0 )
			.attr( 'dy', 14)
			.text( '7-day average' )

		let labelPointer = frame.chart.elem.append('path')
			.attr( 'class', 'c-chart__content u-desktop' )
			.attr( 'stroke', '#767676' )
			.attr( 'stroke-width', '1.5' )
			.attr( 'd', `M${labelX} ${labelY - 10}, L${labelX - 10} ${labelY - 30}`)
	}
}

export {
	cumulative,
	histogram
}
